import Alert from "@amzn/meridian/alert";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Loader from "@amzn/meridian/loader";
import Responsive from "@amzn/meridian/responsive";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Theme from "@amzn/meridian/theme";
import { observer } from "mobx-react-lite";
import React from "react";

import { ColorCodes } from "../constants/ColorCodes";
import { FooterButton } from "../constants/Footer";
import { ScreenSize } from "../constants/ScreenSize";
import { commonUnityStore } from "../store/CommonUnityStore";
import { profileFooterStyle } from "../styles/Style";
import { IFooterButtonList, IFooterProps } from "../types/Footer.types";
import { FooterTokens } from "../utils/Tokens";
import DiscardModal from "./DiscardModal";

const Footer = ({
  handleDisableDiscardChanges,
  handleDisableSaveChanges,
  handleClickDiscardChanges,
  handleClickSaveChanges,
  isDisableChangesLoading,
  isSaveChangesLoading,
  discardButtonText,
  saveChangesButtonText,
  isNormalTheme,
  constraintText,
}: IFooterProps): React.JSX.Element => {
  const { DEFAULT_DISCARD_CHANGES, DEFAULT_SAVE_CHANGES } = FooterButton;
  const { handleChangeDiscardModal } = commonUnityStore;
  const { WHITE } = ColorCodes;

  const footerButtonList: IFooterButtonList[] = [
    {
      label:  discardButtonText || DEFAULT_DISCARD_CHANGES,
      onClick: () => handleChangeDiscardModal(true),
      disable: handleDisableDiscardChanges(),
      isLoading: isDisableChangesLoading || false,
      type: "secondary"
    },
    {
      label:  saveChangesButtonText || DEFAULT_SAVE_CHANGES,
      onClick: handleClickSaveChanges,
      disable: handleDisableSaveChanges(),
      isLoading: isSaveChangesLoading || false,
      type: isNormalTheme ? "primary" : "tertiary"
    },
  ];

  return (
    <Responsive
      query="max-width"
      props={{
        showMobileView: {
          default: false,
          [ScreenSize._720]: true,
        },
      }}
    >
      {({ showMobileView }) => (
        <Theme
          tokens={FooterTokens}
        >
          <Column
            width="100%"
            data-testid="footer-container-column"
            className={profileFooterStyle}
            backgroundColor={WHITE}
            alignmentVertical="center"
          >
            <Row data-testid="footer-content-row"
              alignmentHorizontal={showMobileView ? "center" : "end"}
              alignmentVertical="center"
              wrap={showMobileView ? "down" : "none"}
              widths={showMobileView ? ["100%", "100%"] : ["fit-content", "fit-content"]}
            > 
              {constraintText?.length > 0 && (
                <Column alignmentHorizontal="center" data-testid="footer-constraint-column">
                  <Alert size="small" width="fit-content" data-testid="footer-constraint-alert">
                    <Text type="b200" color="secondary" data-testid="footer-constraint-text">{constraintText}</Text>
                  </Alert>
                </Column>
              )}
              <Row widths={showMobileView && ["50%", "50%"]} width={showMobileView ? "100%" : "fit-content"} data-testid="footer-buttons-row">
                {footerButtonList.map(({
                  label,
                  onClick,
                  disable,
                  isLoading,
                  type,
                }: IFooterButtonList) => (
                  <Button
                    data-testid={`footer-${label.toLowerCase().replace(/\s+/g, "-")}-button`}
                    disabled={disable}
                    onClick={onClick}
                    type={type}
                    key={label}
                  >
                    {isLoading ? <Loader size="small" data-testid={`footer-${label.toLowerCase().replace(/\s+/g, "-")}-loader`} /> : label}
                  </Button>
                ))}
              </Row>
            </Row>
          </Column>
          <DiscardModal handleResetChanges={handleClickDiscardChanges} />
        </Theme>
      )}
    </Responsive>
  );
};

export default observer(Footer);

